import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  Card,
  QueueResponse,
  CardHistoryResponse,
  OutboxItem,
  Stats,
  StatsTimeframe,
  SectionCounts,
  InboxHealth,
  HatMailUser,
  CardAttachment,
  OutboundAttachment,
  RedraftReason,
  EscalateTo,
  Inbox,
  ShadowComparison,
  ShadowStats,
  ShadowReviewBody,
  ShadowListResponse,
  ActivityResponse,
  ConversationResponse,
  ConversationAttachment,
  EmailTemplate,
  EmailTemplateKey,
  OutboundComposition,
} from '../../../../types/hatmail';
import { buildCardQueryParams, type CardSortMode } from '../../../card-query';

// ── API Base ────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const DEVICE_ID_KEY = 'hatmail_device_id';

export function getHatMailDeviceId(): string {
  const existing = localStorage.getItem(DEVICE_ID_KEY);
  if (existing) return existing;
  const generated =
    typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(DEVICE_ID_KEY, generated);
  return generated;
}

export async function apiFetch<T = unknown>(path: string, options?: RequestInit): Promise<T> {
  const token = localStorage.getItem('hatmail_token');
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(msg || `API error: ${res.status}`);
  }
  return res.json();
}

async function authenticatedFetch(path: string, options?: RequestInit): Promise<Response> {
  const token = localStorage.getItem('hatmail_token');
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...options,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(msg || `API error: ${res.status}`);
  }
  return res;
}

export async function fetchAttachmentBlob(path: string): Promise<Blob> {
  const res = await authenticatedFetch(path);
  return res.blob();
}

export async function uploadOutboundAttachment(
  responseId: string,
  file: File,
): Promise<OutboundAttachment> {
  const form = new FormData();
  form.append('file', file);
  const res = await authenticatedFetch(`/attachments/outbound/${responseId}`, {
    method: 'POST',
    body: form,
  });
  return res.json();
}

export async function deleteOutboundAttachment(attachmentId: string): Promise<void> {
  await authenticatedFetch(`/attachments/outbound/${attachmentId}`, {
    method: 'DELETE',
  });
}

function qs(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

// ── Sort ────────────────────────────────────────────────────

export type SortMode = CardSortMode;

// ── Query Hooks ─────────────────────────────────────────────

export function useCards(inbox: Inbox | undefined = 'retail', sort: SortMode = 'smart', search = '') {
  const params = buildCardQueryParams(inbox, sort, search);

  return useQuery<QueueResponse>({
    queryKey: ['cards', params.inbox, params.sort, params.search],
    queryFn: () => apiFetch<QueueResponse>(`/cards${qs(params)}`),
    refetchInterval: 10_000,
  });
}

export function useCardHistory(search = '') {
  const normalized = search.trim();

  return useQuery<CardHistoryResponse>({
    queryKey: ['card-history', normalized],
    queryFn: () => apiFetch<CardHistoryResponse>(`/cards/history${qs({ search: normalized })}`),
    enabled: normalized.length > 0,
  });
}

export function useOutbox() {
  return useQuery<OutboxItem[]>({
    queryKey: ['outbox'],
    queryFn: async () => {
      const data = await apiFetch<{ items: OutboxItem[]; count: number }>('/outbox');
      return data.items;
    },
    refetchInterval: 5_000,
  });
}

export function useConversation(responseId: string, enabled = true) {
  return useQuery<ConversationResponse>({
    queryKey: ['conversation', responseId],
    queryFn: () => apiFetch<ConversationResponse>(`/cards/${responseId}/conversation`),
    enabled: enabled && Boolean(responseId),
  });
}

export function useSyncConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (responseId: string) => apiFetch(`/cards/${responseId}/conversation/sync`, { method: 'POST' }),
    onSuccess: (_data, responseId) => {
      qc.invalidateQueries({ queryKey: ['conversation', responseId] });
      qc.invalidateQueries({ queryKey: ['cards'] });
    },
  });
}

export function useEmailTemplates(enabled = true) {
  return useQuery<EmailTemplate[]>({
    queryKey: ['email-templates'],
    queryFn: async () => {
      const data = await apiFetch<{ templates: EmailTemplate[] }>('/email-templates');
      return data.templates;
    },
    enabled,
    retry: false,
  });
}

export function useCreateComposition() {
  return useMutation({
    mutationFn: (body: { template_id: string; fields: Record<string, unknown> }) =>
      apiFetch<{ composition: OutboundComposition }>('/outbound-compositions', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

export function useComposition(id: string, enabled = true) {
  return useQuery<OutboundComposition>({
    queryKey: ['outbound-composition', id],
    queryFn: async () => {
      const data = await apiFetch<{ composition: OutboundComposition }>(`/outbound-compositions/${id}`);
      return data.composition;
    },
    enabled: enabled && Boolean(id),
    refetchInterval: 5_000,
  });
}

export function useUpdateComposition() {
  return useMutation({
    mutationFn: ({ id, ...body }: {
      id: string;
      fields?: Record<string, unknown>;
      final_subject?: string;
      final_body?: string;
    }) => apiFetch<{ composition: OutboundComposition }>(`/outbound-compositions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  });
}

export function useGenerateComposition() {
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ composition: OutboundComposition }>(
      `/outbound-compositions/${id}/generate`,
      { method: 'POST' },
    ),
  });
}

export function useQueueComposition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<{
      outbox_id: string;
      composition_id: string;
      scheduled_send_at: string;
      duplicate: boolean;
    }>(`/outbound-compositions/${id}/queue`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outbox'] }),
  });
}

export function useStats(timeframe: StatsTimeframe = 'today') {
  return useQuery<Stats>({
    queryKey: ['stats', timeframe],
    queryFn: () => apiFetch<Stats>(`/stats${qs({ timeframe })}`),
  });
}

export function useActivity(timeframe: StatsTimeframe = 'today') {
  return useQuery<ActivityResponse>({
    queryKey: ['activity', timeframe],
    queryFn: () => apiFetch<ActivityResponse>(`/activity${qs({ timeframe })}`),
    refetchInterval: 30_000,
  });
}

export function useSectionCounts() {
  return useQuery<SectionCounts>({
    queryKey: ['section-counts'],
    queryFn: () => apiFetch<SectionCounts>('/sections/counts'),
    refetchInterval: 10_000,
  });
}

export function useInboxHealth(inbox: Inbox = 'retail') {
  return useQuery<InboxHealth>({
    queryKey: ['inbox-health', inbox],
    queryFn: () => apiFetch<InboxHealth>(`/sections/inbox-health${qs({ inbox })}`),
    refetchInterval: 10_000,
  });
}

export function useReconnectInbox() {
  return useMutation({
    mutationFn: async (inbox: 'retail' | 'shop') => {
      const data = await apiFetch<{ auth_url: string }>(
        `/admin/inbox-connections/${inbox}/reconnect-url`,
        { method: 'POST' },
      );
      return data.auth_url;
    },
    onSuccess: (authUrl) => {
      window.location.href = authUrl;
    },
  });
}

export function useSentToday() {
  return useQuery<Card[]>({
    queryKey: ['sent-today'],
    queryFn: () => apiFetch<Card[]>('/sections/sent'),
  });
}

export function useUser() {
  return useQuery<HatMailUser>({
    queryKey: ['user'],
    queryFn: async () => {
      const base = API_BASE.replace(/\/api$/, '');
      const token = localStorage.getItem('hatmail_token');
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${base}/auth/me`, {
        credentials: 'include',
        headers,
      });
      if (!res.ok) throw new Error('Not authenticated');
      return res.json();
    },
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const base = API_BASE.replace(/\/api$/, '');
      const token = localStorage.getItem('hatmail_token');
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${base}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers,
      });
      if (!res.ok) throw new Error('Logout failed');
      return res.json();
    },
    onSuccess: () => {
      localStorage.removeItem('hatmail_token');
      qc.clear();
      window.location.href = '/';
    },
  });
}

export function useSwitchStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const base = API_BASE.replace(/\/api$/, '');
      const token = localStorage.getItem('hatmail_token');
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${base}/auth/switch-staff`, {
        method: 'POST',
        credentials: 'include',
        headers,
      });
      if (!res.ok) throw new Error('Could not switch staff');
      return res.json();
    },
    onSuccess: () => {
      localStorage.removeItem('hatmail_token');
      qc.clear();
      window.location.href = '/';
    },
  });
}

export function usePinLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pin: string) => {
      const base = API_BASE.replace(/\/api$/, '');
      const res = await fetch(`${base}/auth/pin-login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-HatMail-Device-Id': getHatMailDeviceId(),
        },
        body: JSON.stringify({ pin }),
      });
      if (!res.ok) {
        throw new Error('Invalid PIN');
      }
      return res.json() as Promise<{ token: string; user: HatMailUser }>;
    },
    onSuccess: (data) => {
      localStorage.setItem('hatmail_token', data.token);
      qc.setQueryData(['user'], data.user);
      qc.invalidateQueries({ queryKey: ['user'] });
    },
  });
}

// ── Mutation Hooks ──────────────────────────────────────────

interface SendResult {
  outbox_id: string;
  response_id: string;
  scheduled_send_at: string;
}

export function useSendCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<SendResult>(`/cards/${id}/send`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cards'] });
      qc.invalidateQueries({ queryKey: ['outbox'] });
      qc.invalidateQueries({ queryKey: ['section-counts'] });
    },
  });
}

export function useEditCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      edited_body,
      attachment_ids,
    }: {
      id: string;
      edited_body: string;
      attachment_ids?: string[];
    }) =>
      apiFetch(`/cards/${id}/edit`, {
        method: 'POST',
        body: JSON.stringify({ edited_body, attachment_ids }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cards'] });
    },
  });
}

export function useRedraftCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason, note }: { id: string; reason: RedraftReason; note?: string }) =>
      apiFetch(`/cards/${id}/redraft`, {
        method: 'POST',
        body: JSON.stringify({ reason, note }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cards'] });
    },
  });
}

export function useEscalateCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, escalate_to, note }: { id: string; escalate_to: EscalateTo; note?: string }) =>
      apiFetch(`/cards/${id}/escalate`, {
        method: 'POST',
        body: JSON.stringify({ escalate_to, note }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cards'] });
      qc.invalidateQueries({ queryKey: ['card-history'] });
      qc.invalidateQueries({ queryKey: ['section-counts'] });
    },
  });
}

export function useClearCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      apiFetch(`/cards/${id}/clear`, {
        method: 'POST',
        body: JSON.stringify({ note }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cards'] });
      qc.invalidateQueries({ queryKey: ['card-history'] });
      qc.invalidateQueries({ queryKey: ['section-counts'] });
    },
  });
}

export function useLockCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/cards/${id}/lock`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cards'] });
    },
  });
}

export function useUnlockCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/cards/${id}/lock`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cards'] });
    },
  });
}

export function useBatchSend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (response_ids: string[]) =>
      apiFetch<{ sent: number; outbox_ids: string[] }>('/cards/batch-send', {
        method: 'POST',
        body: JSON.stringify({ response_ids }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cards'] });
      qc.invalidateQueries({ queryKey: ['outbox'] });
      qc.invalidateQueries({ queryKey: ['section-counts'] });
    },
  });
}

export function useCancelSend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (outboxId: string) =>
      apiFetch(`/outbox/${outboxId}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['outbox'] });
      qc.invalidateQueries({ queryKey: ['cards'] });
      qc.invalidateQueries({ queryKey: ['section-counts'] });
    },
  });
}

// ── Shadow Mode Hooks ──────────────────────────────────────

export function useShadowComparisons(limit = 50, offset = 0) {
  return useQuery<ShadowListResponse>({
    queryKey: ['shadow', limit, offset],
    queryFn: () => apiFetch<ShadowListResponse>(`/shadow${qs({ limit, offset })}`),
    refetchInterval: 30_000,
  });
}

export function useShadowStats() {
  return useQuery<ShadowStats>({
    queryKey: ['shadow-stats'],
    queryFn: () => apiFetch<ShadowStats>('/shadow/stats'),
    refetchInterval: 30_000,
  });
}

export function useReviewShadow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & ShadowReviewBody) =>
      apiFetch(`/shadow/${id}/review`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shadow'] });
      qc.invalidateQueries({ queryKey: ['shadow-stats'] });
    },
  });
}

// ── Feedback Hooks ────────────────────────────────────────────

export type FeedbackType = 'tone' | 'wrong_info' | 'missing_detail' | 'other';

export function useSubmitFeedback() {
  return useMutation({
    mutationFn: ({ response_id, feedback_type, note }: { response_id: string; feedback_type: FeedbackType; note?: string }) =>
      apiFetch('/feedback', {
        method: 'POST',
        body: JSON.stringify({ response_id, feedback_type, note }),
      }),
  });
}

// Re-export types the components need
export type {
  Card,
  CardAttachment,
  OutboundAttachment,
  OutboxItem,
  Stats,
  StatsTimeframe,
  SectionCounts,
  InboxHealth,
  HatMailUser,
  Inbox,
  RedraftReason,
  EscalateTo,
  ShadowComparison,
  ShadowStats,
  ActivityResponse,
  ConversationResponse,
  ConversationAttachment,
  EmailTemplate,
  EmailTemplateKey,
  OutboundComposition,
};
